import { SelectProps, Tooltip } from 'antd';

import { useEffect, useState } from 'react';
import { useDebounce } from '@/hooks';
import InfiniteSelect from '@/components/_common/InfiniteSelect';
import {
  useDocumentTypeReduced,
  useDocumentTypesReduced,
} from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.hooks';
import {
  DocumentTypeReduced,
  ListDocumentTypesQueryReduced,
} from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.types';
import useSelectOptions from '@/hooks/useSelectOptions';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { AlertCircle } from 'react-feather';
import Group from '../Group';

type Props = Omit<SelectProps, 'options'> & {
  queryFilter?: ListDocumentTypesQueryReduced;
  queryEnable?: boolean;
  onDocumentTypeSelected?: (documentType: DocumentTypeReduced) => void;
  hasFinancial?: boolean;
};

const DocumentTypesInfiniteSelectField: React.FC<Props> = ({
  queryFilter,
  queryEnable = false,
  hasFinancial = false,
  ...props
}) => {
  const [selectClicked, setSelectClicked] = useState<boolean>(queryEnable);
  const [searchValue, setSearchValue] = useState<string>();
  const search = useDebounce(searchValue);

  const documentTypeList = useDocumentTypesReduced(
    {
      active: true,
      rows: 10,
      description: search,
      ...queryFilter,
    },
    (selectClicked || queryEnable) && !props.disabled,
  );

  const data = flatInfiniteData(documentTypeList.data);

  const documentTypeDetail = useDocumentTypeReduced();

  const documentTypesOptions = useSelectOptions({
    list: documentTypeList,
    detail: documentTypeDetail,
    selectProps: props,
    labelFormatter: (document) => (
      <Group gap={5}>
        <div
          style={{
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {document.description}
        </div>

        {hasFinancial && document.notGenerateFinancial && (
          <Tooltip title="Esta opção está desabilitada porque já existe um financeiro gerado, e este tipo de documento não permite geração financeira.">
            <AlertCircle
              size={16}
              color="orange"
              style={{ cursor: 'context-menu', opacity: 0.5 }}
            />
          </Tooltip>
        )}
      </Group>
    ),
    disableCondition: (document) =>
      hasFinancial && document.notGenerateFinancial,
  });

  useEffect(() => {
    if (
      documentTypeDetail.data?.id &&
      documentTypeDetail.data.id === props.value
    ) {
      props.onDocumentTypeSelected?.(documentTypeDetail.data);
    }
  }, [documentTypeDetail.data, props.value]);

  return (
    <InfiniteSelect
      fetchNextPage={documentTypeList.fetchNextPage}
      hasNextPage={documentTypeList.hasNextPage}
      loading={documentTypeList.isFetching}
      onSelect={(documentTypeId: number) => {
        const documentTypeSelected = data.find(
          ({ id }) => id === documentTypeId,
        );

        if (!!documentTypeSelected)
          props?.onDocumentTypeSelected?.(documentTypeSelected);

        setSelectClicked(false);
      }}
      onDropdownVisibleChange={(open) => setSelectClicked(open)}
      options={documentTypesOptions}
      showSearch
      searchValue={searchValue}
      filterOption={false}
      onSearch={setSearchValue}
      {...props}
    />
  );
};

export default DocumentTypesInfiniteSelectField;
